import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ExternalLink,
  Linkedin,
  Loader2,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  Plus,
  Send,
  Share2,
  Trash2,
  Twitter,
} from "lucide-react";
import { toast } from "sonner";
import {
  raiseBoardApi,
  type RaiseBoardBySlugResponse,
  type RaiseBoardComment,
  type RaiseBoardCommentOrigin,
} from "@/api/raise-board-api";
import { lookbookApi, type LookbookPublicResponse } from "@/api/lookbook-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getErrorMessage } from "@/lib/api-error";
import {
  getGuestName,
  getVisitorId,
  setGuestName,
} from "@/lib/lookbook-access";
import { getStageText, GICSIndustries, GICSSubIndustries } from "@/lib/utils";
import { CompanyAvatar } from "./company-avatar";
import { UpvoteButton } from "./upvote-button";
import {
  MetaRow,
  RaiseBoardNav,
  RaiseBoardShell,
  RBCard,
  RBTag,
  SectionLabel,
  StatusChip,
} from "./rb-ui";
import {
  useAddToPipeline,
  useMyVotes,
  useToggleUpvote,
} from "./use-raise-board-actions";
import {
  resolveLookbookVisibility,
  SECTION_ANCHORS,
} from "./lookbook-visibility";
import { LookbookDataRoom } from "./lookbook-data-room";
import { LookbookReports } from "./lookbook-reports";
import { LookbookSectionNav } from "./lookbook-section-nav";

type Application =
  RaiseBoardBySlugResponse["result"]["applications"][number];

function getCompany(application: Application) {
  const ac = application.applicant_company;
  if (!ac || typeof ac === "string") return null;
  return ac;
}

function toArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function pickList(
  appValue: string | string[] | undefined,
  companyValue: string | string[] | undefined
): string[] {
  const fromApp = toArray(appValue);
  return fromApp.length ? fromApp : toArray(companyValue);
}

function dealInfoStr(
  application: Application,
  fieldName: string
): string | undefined {
  const v = application.deal_info?.find(
    (f) => f.field_name === fieldName
  )?.field_value;
  if (typeof v === "string" && v.trim()) return v;
  if (Array.isArray(v) && v.length > 0) return v.filter(Boolean).join(", ");
  if (typeof v === "number") return String(v);
  return undefined;
}

function formatRaiseAmount(amount?: string, currency?: string) {
  if (!amount) return null;
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const ccy = currency?.trim();
  return ccy ? `${trimmed} ${ccy}` : trimmed;
}

function resolveRaise(application: Application): string | null {
  const amount =
    dealInfoStr(application, "attempting_to_raise") ||
    (application.attempting_to_raise ?? "").trim();
  const currency =
    dealInfoStr(application, "currency") || (application.currency ?? "").trim();
  return formatRaiseAmount(amount || undefined, currency || undefined);
}

// Readable industries string for the email request.
function resolveIndustries(application: Application): string {
  const company = getCompany(application);
  const subIndustriesField = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  if (subIndustriesField?.field_value) {
    const codes = Array.isArray(subIndustriesField.field_value)
      ? subIndustriesField.field_value
      : [subIndustriesField.field_value as string];
    const names = codes
      .map((c) => GICSSubIndustries.find((si) => si.code === String(c))?.name)
      .filter((n): n is string => !!n);
    if (names.length > 0) return names.join(", ");
  }
  const fallback = application.industry ?? company?.industry ?? [];
  return fallback.filter(Boolean).join(", ");
}

function uniq(values: string[]): string[] {
  return values.filter((v, i, arr) => arr.indexOf(v) === i);
}

/** The workspace board a deal was published on, as populated on the payload. */
function getSourceBoard(application: Application): {
  slug?: string;
  enabled?: boolean;
} {
  const p = application.pertains_to_opportunity;
  if (!p || typeof p === "string") return {};
  return { slug: p.portal_slug, enabled: p.raise_board_enabled };
}

function findApplication(
  data: { result?: { applications?: Application[] } } | undefined,
  companyId: string
): Application | null {
  const apps = data?.result?.applications ?? [];
  return (
    apps.find((a) => {
      const ac = a.applicant_company;
      const id = typeof ac === "string" ? ac : ac?._id;
      return String(id) === companyId;
    }) ?? null
  );
}

export default function RaiseBoardDealProfile() {
  // Serves both /rb/:slug/:companyId and /lb/:lookbookPublicId/:companyId — the same
  // profile, differing only in where its deal is read from and where "back"
  // goes. Exactly one of slug / lookbookPublicId is ever set.
  const { slug, lookbookPublicId, companyId } = useParams<{
    slug: string;
    lookbookPublicId: string;
    companyId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the global board when it navigates here. Absent on a cold-loaded
  // shared link, which correctly falls back to the deal's own board.
  const originFrom = (location.state as { from?: string } | null)?.from;
  // A lookbook wins over origin state: someone who arrived through a private
  // link must land back in that lookbook, not on a public board they may not
  // even be able to see.
  const backTo = lookbookPublicId
    ? `/lb/${lookbookPublicId}`
    : originFrom ?? (slug ? `/rb/${slug}` : "/global");
  const backLabel = lookbookPublicId
    ? "Back to lookbook"
    : backTo === "/global"
      ? "Back to Raiseboard"
      : "Back to board";
  // Recorded on any comment left here. Best effort by design: the global board
  // links to /rb/:slug/:companyId like every other board, so a cold-loaded
  // shared link has no origin state and reads as the public board. It is a
  // label on a shared thread, not a permission, so a wrong guess costs nothing
  // but a badge.
  const commentOrigin: RaiseBoardCommentOrigin = lookbookPublicId
    ? "lookbook"
    : originFrom === "/global"
      ? "global_board"
      : "public_board";
  const { isSignedIn, user } = useUser();
  const [emailing, setEmailing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{ id: string; name: string } | null>(
    null
  );
  const [authReason, setAuthReason] = useState<string | null>(null);

  const addToPipeline = useAddToPipeline();

  // Both endpoints return `result.applications`, so the deal is found the same
  // way either way. The lookbook payload additionally carries the lookbook's
  // own upvote and comment counts in place of the public ones.
  // Named explicitly: the two branches return different shapes, and without the
  // union TS picks the first overload and can't assign the other one.
  const { data, isLoading, error } = useQuery<
    LookbookPublicResponse | RaiseBoardBySlugResponse
  >({
    queryKey: lookbookPublicId ? ["lookbook", lookbookPublicId] : ["raise-board", slug],
    queryFn: () =>
      lookbookPublicId
        ? lookbookApi.getByPublicId(lookbookPublicId)
        : raiseBoardApi.getBySlug(slug as string),
    enabled: !!slug || !!lookbookPublicId,
    retry: false,
  });

  const application = findApplication(data, companyId ?? "");

  // Signed-out visitors vote and comment as guests on a lookbook whose owner
  // allows it. Only a lookbook payload carries the switch, so /rb/ never does.
  const guest =
    !isSignedIn &&
    !!lookbookPublicId &&
    (data as LookbookPublicResponse | undefined)?.result?.lookbook
      ?.guest_engagement === true;
  const votedIds = useMyVotes(!!isSignedIn || guest, lookbookPublicId, guest);
  const toggleVote = useToggleUpvote({ slug, lookbookPublicId, guest });

  // Absent on /rb/ and /global, and on any lookbook entry added before this
  // existed — all of which resolve to profile-only, i.e. today's page.
  const show = resolveLookbookVisibility(application?.sharing);

  // The one-pager email is addressed by the deal's own board slug, which a
  // lookbook URL doesn't carry — read it off the deal instead. The endpoint
  // also requires that board to be publicly enabled, so a lookbook drawn from
  // a workspace with its board switched off hides the action rather than
  // offering a button that can only fail.
  const source = application ? getSourceBoard(application) : null;
  const emailSlug = slug ?? source?.slug;
  const canEmail = Boolean(emailSlug && (slug || source?.enabled));

  const sendEmail = useMutation({
    mutationFn: async (email: string) => {
      if (!emailSlug || !application) throw new Error("Missing deal");
      return raiseBoardApi.sendDealEmail({
        slug: emailSlug,
        applicationId: String(application._id),
        recipientEmail: email,
        industries: resolveIndustries(application),
      });
    },
    onSuccess: (_data, email) => {
      toast.success(`Deal details sent to ${email}.`);
      setEmailing(false);
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, "Failed to send deal details"));
    },
  });

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  const applicationId = application ? String(application._id) : "";
  const voted = votedIds.has(applicationId);
  const voteCount = application?.upvote_count ?? 0;

  const handleVote = () => {
    if (!isSignedIn && !guest) {
      setAuthReason("Sign in to upvote this deal.");
      return;
    }
    if (applicationId) toggleVote.mutate(applicationId);
  };

  const handleAddToPipeline = () => {
    if (!application) return;
    const company = getCompany(application);
    const ac = application.applicant_company;
    const id = typeof ac === "string" ? ac : ac?._id;
    if (!id) return;
    setPendingAdd({
      id: String(id),
      name: company?.company_name ?? "This company",
    });
  };

  return (
    <RaiseBoardShell>
      <RaiseBoardNav onRequestAuth={setAuthReason} />

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </button>

        {isLoading ? (
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <div className="min-w-0 flex-1 space-y-4">
              <Skeleton className="h-52 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
            </div>
            <div className="w-full shrink-0 lg:w-[340px]">
              <Skeleton className="h-60 w-full rounded-2xl" />
            </div>
          </div>
        ) : error || !application ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
            <Megaphone className="h-10 w-10 text-muted-foreground/40" />
            <p className="font-serif text-2xl">This deal isn't available</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              It may have been removed from the board.
            </p>
            <Button
              variant="outline"
              className="mt-1 rounded-full bg-card"
              onClick={() => navigate("/global")}
            >
              Browse the global board
            </Button>
          </div>
        ) : (
          /* Two columns on desktop, one on mobile. The rail is placed between
             the two content blocks in source order so that on a phone the
             capital status and the primary action land right under the company
             card instead of below the whole comment thread. */
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="min-w-0 space-y-4 lg:col-start-1 lg:row-start-1">
              <DealIdentityCard
                application={application}
                voted={voted}
                voteCount={voteCount}
                onVote={handleVote}
              />

              <LookbookSectionNav sections={show.sections} />

              {show.profile && (
                <DealNarrative
                  application={application}
                  id={SECTION_ANCHORS.profile}
                />
              )}

              {show.sections.includes("data-room") && lookbookPublicId && (
                <LookbookDataRoom
                  id={SECTION_ANCHORS["data-room"]}
                  files={show.files}
                  publicId={lookbookPublicId}
                  companyId={companyId as string}
                />
              )}

              {show.sections.includes("reports") && lookbookPublicId && (
                <LookbookReports
                  id={SECTION_ANCHORS.reports}
                  reportKeys={show.reportKeys}
                  publicId={lookbookPublicId}
                  companyId={companyId as string}
                />
              )}
            </div>

            <DealSidebar
              application={application}
              copied={copied}
              onShare={handleShare}
              onEmail={() => setEmailing(true)}
              canEmail={canEmail}
              onAddToPipeline={handleAddToPipeline}
              showDetails={show.profile}
            />

            <div
              id="lb-comments"
              className="min-w-0 space-y-4 lg:col-start-1 lg:row-start-2"
            >
              <CommentsSection
                applicationId={String(application._id)}
                lookbookPublicId={lookbookPublicId}
                origin={commentOrigin}
                guest={guest}
              />
            </div>
          </div>
        )}
      </main>

      <SendEmailDialog
        open={emailing}
        application={application}
        defaultEmail={user?.primaryEmailAddress?.emailAddress ?? ""}
        isSending={sendEmail.isPending}
        onSend={(email) => sendEmail.mutate(email)}
        onClose={() => setEmailing(false)}
      />

      <AlertDialog
        open={!!pendingAdd}
        onOpenChange={(open) => {
          if (!open) setPendingAdd(null);
        }}
      >
        <AlertDialogContent>
          {isSignedIn ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Add this company to your pipeline?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <span className="font-medium">{pendingAdd?.name}</span> will be
                  added as a deal in your DiligenceGPT pipeline, where you can
                  review it and run due diligence.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    if (pendingAdd) addToPipeline.mutate(pendingAdd.id);
                    setPendingAdd(null);
                  }}
                >
                  Add to Pipeline
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Add this company to your pipeline
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Adding <span className="font-medium">{pendingAdd?.name}</span>{" "}
                  to your pipeline requires a DiligenceGPT account. Get started
                  to review this deal and run your own due diligence.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="sm:justify-between">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setPendingAdd(null);
                    setAuthReason("Sign in to add this deal to your pipeline.");
                  }}
                >
                  Get started
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      <BoardAuthDialog
        open={!!authReason}
        onOpenChange={(open) => {
          if (!open) setAuthReason(null);
        }}
        reason={authReason ?? undefined}
      />
    </RaiseBoardShell>
  );
}

/**
 * Every derived display value for one deal, resolved once so the identity
 * card and the sidebar can't disagree about what the deal says.
 */
function useDealDetails(application: Application) {
  const company = getCompany(application);

  // Every approved GICS code the deal carries, resolved to both its
  // sub-industry name and its parent industry. All of them, not just the
  // first — a company tagged with three sub-industries should show three.
  const subIndustriesField = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  const codes: string[] = subIndustriesField?.field_value
    ? (Array.isArray(subIndustriesField.field_value)
        ? subIndustriesField.field_value
        : [subIndustriesField.field_value]
      )
        .map(String)
        .filter(Boolean)
    : [];

  const subIndustries = uniq(
    codes
      .map((c) => GICSSubIndustries.find((si) => si.code === c)?.name)
      .filter((n): n is string => !!n)
  );
  let industries = uniq(
    codes
      .map((c) =>
        c.length >= 8
          ? GICSIndustries.find((i) => i.code === c.substring(0, 8))?.name
          : undefined
      )
      .filter((n): n is string => !!n)
  );
  if (industries.length === 0) {
    industries = (application.industry ?? company?.industry ?? []).filter(
      Boolean
    );
  }

  const dealInfoCountry = application.deal_info?.find(
    (f) => f.field_name === "address"
  )?.field_value as string | undefined;

  const stageValue = application.stage ?? company?.stage;
  const stageLabel = stageValue != null ? getStageText(stageValue) : null;

  const website = company?.company_website ?? application.company_website;

  const source = (() => {
    const p = application.pertains_to_opportunity;
    if (!p || typeof p === "string") return {};
    return {
      name: p.opportunity_name,
      slug: p.portal_slug,
      enabled: p.raise_board_enabled,
    };
  })();

  return {
    company,
    name: company?.company_name ?? "Untitled",
    bio: company?.short_bio ?? application.short_bio,
    country: dealInfoCountry || company?.country || application.country,
    industries,
    subIndustries,
    stageLabel: stageLabel && stageLabel !== "Unknown" ? stageLabel : null,
    raise: resolveRaise(application),
    businessModels: pickList(application.business_model, company?.business_model),
    revenueModels: pickList(application.revenue_model, company?.revenue_model),
    founded: application.year_of_inception,
    website,
    websiteHost: website
      ? website.replace(/^https?:\/\//, "").replace(/\/$/, "")
      : null,
    websiteHref: website
      ? website.startsWith("http")
        ? website
        : `https://${website}`
      : null,
    linkedin: application.linkedIn,
    twitter: application.twitter,
    source,
  };
}

/**
 * The company itself: logo, name, one-liner, classification, links, upvote.
 *
 * Always rendered, even when a lookbook hides the profile. A page that opens on
 * a bare file list with no company name is unusable, and everything here was
 * already on the card the visitor clicked to get here — so hiding it would cost
 * usability and protect nothing.
 */
function DealIdentityCard({
  application,
  voted,
  voteCount,
  onVote,
}: {
  application: Application;
  voted: boolean;
  voteCount: number;
  onVote: () => void;
}) {
  const d = useDealDetails(application);

  const links: Array<{ label: string; href: string; icon: typeof Linkedin }> =
    [];
  if (d.linkedin)
    links.push({
      label: "LinkedIn",
      href: d.linkedin.startsWith("http") ? d.linkedin : `https://${d.linkedin}`,
      icon: Linkedin,
    });
  if (d.twitter)
    links.push({
      label: "Twitter",
      href: d.twitter.startsWith("http") ? d.twitter : `https://${d.twitter}`,
      icon: Twitter,
    });

  // Every classification the deal carries. Shown in full — the profile has the
  // room, and these are the same values the board filters on.
  const tags = uniq(
    [...d.industries, ...d.subIndustries, ...d.businessModels].filter(Boolean)
  );

  return (
    <>
      <RBCard>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <CompanyAvatar
            logoUrl={d.company?.logo_url}
            name={d.company?.company_name}
            size="2xl"
            tone="soft"
          />
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-3xl leading-tight">{d.name}</h1>
            {d.bio && (
              <p className="mt-1 text-[15px] text-muted-foreground">{d.bio}</p>
            )}

            <MetaRow
              className="mt-2 text-[13px]"
              items={[
                d.country && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    {d.country}
                  </span>
                ),
                d.founded && `Founded ${d.founded}`,
              ]}
            />

            {tags.length > 0 && (
              <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
                {tags.map((t) => (
                  <RBTag key={t}>{t}</RBTag>
                ))}
              </div>
            )}

            {(d.revenueModels.length > 0 || d.stageLabel) && (
              <div className="mt-3.5 space-y-1 text-[13px]">
                {d.revenueModels.length > 0 && (
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">
                      Revenue model
                    </span>{" "}
                    · {d.revenueModels.join(" · ")}
                  </p>
                )}
                {d.stageLabel && (
                  <p className="text-muted-foreground">
                    Stage:{" "}
                    <span className="font-medium text-foreground">
                      {d.stageLabel}
                    </span>
                  </p>
                )}
              </div>
            )}

            {(d.websiteHref || links.length > 0) && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {d.websiteHref && d.websiteHost && (
                  <a
                    href={d.websiteHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium underline-offset-4 hover:underline"
                  >
                    View full profile
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {links.map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Endorsing is the one thing a visitor comes here to do, so it sits
              with the company's name instead of below the fold. Last in source
              order: with items-start that lands it top-right beside the flex-1
              identity block on desktop, and under it on a phone. */}
          <div className="shrink-0">
            <UpvoteButton
              variant="outline"
              count={voteCount}
              voted={voted}
              onClick={onVote}
            />
          </div>
        </div>
      </RBCard>
    </>
  );
}

/**
 * The pitch: round overview, then problem / solution / traction.
 *
 * This — not the identity card — is what a lookbook's "Profile" switch turns
 * off. The server strips these fields from the payload when it is off, so this
 * renders nothing on its own; the flag is belt and braces.
 */
function DealNarrative({
  application,
  id,
}: {
  application: Application;
  id?: string;
}) {
  const d = useDealDetails(application);

  const pitchSections: Array<{ label: string; body: string }> = [];
  if (application.problem)
    pitchSections.push({ label: "Problem", body: application.problem });
  if (application.solution)
    pitchSections.push({ label: "Solution", body: application.solution });
  if (application.traction)
    pitchSections.push({ label: "Traction", body: application.traction });

  // The anchor sits on a wrapper rather than the first card, which is
  // conditional — a deal with no raise and no objective would otherwise leave
  // the section nav pointing at nothing.
  return (
    <div id={id} className="space-y-4">
      {(d.raise || application.objective) && (
        <RBCard>
          <SectionLabel>Round Overview</SectionLabel>
          {d.raise && (
            <p className="mt-3 font-serif text-2xl leading-tight">
              Raising {d.raise}
              {d.stageLabel ? ` · ${d.stageLabel}` : ""}
            </p>
          )}
          {application.objective && (
            <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-foreground/80">
              {application.objective}
            </p>
          )}
        </RBCard>
      )}

      {pitchSections.map((s) => (
        <RBCard key={s.label}>
          <SectionLabel>{s.label}</SectionLabel>
          <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-foreground/80">
            {s.body}
          </p>
        </RBCard>
      ))}
    </div>
  );
}

/** Right rail: capital status at a glance, then the actions. */
function DealSidebar({
  application,
  copied,
  onShare,
  onEmail,
  canEmail,
  onAddToPipeline,
  showDetails = true,
}: {
  application: Application;
  copied: boolean;
  onShare: () => void;
  onEmail: () => void;
  /** False when the deal's own board isn't public, so the email can't be sent. */
  canEmail: boolean;
  onAddToPipeline: () => void;
  /**
   * False when a lookbook has the profile switched off. Hides the expandable
   * detail list — industries, business model, revenue model are profile facts.
   * The capital status and the actions stay in every configuration.
   */
  showDetails?: boolean;
}) {
  const d = useDealDetails(application);
  const [expanded, setExpanded] = useState(false);

  const details: Array<{ label: string; value: string }> = [];
  if (d.country) details.push({ label: "Country", value: d.country });
  if (d.founded) details.push({ label: "Founded", value: d.founded });
  if (d.industries.length > 0)
    details.push({
      label: d.industries.length > 1 ? "Industries" : "Industry",
      value: d.industries.join(", "),
    });
  // Only the sub-industries that add something over the parent names above.
  const extraSubs = d.subIndustries.filter((s) => !d.industries.includes(s));
  if (extraSubs.length > 0)
    details.push({
      label: extraSubs.length > 1 ? "Sub-industries" : "Sub-industry",
      value: extraSubs.join(", "),
    });
  if (d.businessModels.length > 0)
    details.push({ label: "Business model", value: d.businessModels.join(", ") });
  if (d.revenueModels.length > 0)
    details.push({ label: "Revenue model", value: d.revenueModels.join(", ") });

  return (
    <aside className="w-full space-y-3 lg:sticky lg:top-24 lg:col-start-2 lg:row-start-1 lg:row-span-2">
      <div className="flex items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
        <StatusChip size="md">Actively Raising</StatusChip>
      </div>

      <RBCard>
        <SectionLabel>Capital Status</SectionLabel>
        <p className="mt-3 font-serif text-xl leading-tight">
          {d.raise ? `Raising ${d.raise}` : "Raise amount undisclosed"}
        </p>
        <MetaRow
          className="mt-1.5 text-[13px]"
          items={[
            d.stageLabel,
            d.source.name && `via ${d.source.name}`,
          ]}
        />

        {showDetails && details.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  expanded ? "rotate-180" : ""
                }`}
              />
              {expanded ? "Less" : "More"} details
            </button>
            {expanded && (
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-border pt-4">
                {details.map((item) => (
                  <div key={item.label}>
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      {item.label}
                    </dt>
                    <dd className="mt-1 text-sm font-medium">{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </>
        )}
      </RBCard>

      <div className="space-y-2">
        <Button className="w-full rounded-xl" onClick={onAddToPipeline}>
          <Plus className="h-4 w-4" />
          Add to Pipeline
        </Button>
        <div className="flex gap-2">
          {canEmail && (
            <Button
              variant="outline"
              className="flex-1 rounded-xl bg-card"
              onClick={onEmail}
            >
              <Mail className="h-4 w-4" />
              Email me this
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 rounded-xl bg-card"
            onClick={onShare}
            aria-label={copied ? "Link copied" : "Copy share link"}
            title={copied ? "Copied" : "Share"}
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Share2 className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </aside>
  );
}

function SendEmailDialog({
  open,
  application,
  defaultEmail,
  isSending,
  onSend,
  onClose,
}: {
  open: boolean;
  application: Application | null;
  defaultEmail: string;
  isSending: boolean;
  onSend: (email: string) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  // Seed only when the dialog opens. Depending on defaultEmail too would wipe
  // what the user is typing if Clerk resolves the address late.
  useEffect(() => {
    if (open) setEmail(defaultEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open || !application) return null;
  const company = getCompany(application);
  const name = company?.company_name ?? "this company";
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>Email deal details</DialogTitle>
        <p className="text-sm text-muted-foreground">
          Send <span className="font-medium text-foreground">{name}</span>'s
          details to:
        </p>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && valid && !isSending) onSend(email.trim());
          }}
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onSend(email.trim())}
            disabled={!valid || isSending}
          >
            {isSending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-1.5 h-4 w-4" />
            )}
            Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const COMMENT_MAX = 2000;

function CommentsSection({
  applicationId,
  lookbookPublicId,
  origin,
  guest = false,
}: {
  applicationId: string;
  /** Set on a lookbook profile: the thread there is private to that lookbook. */
  lookbookPublicId?: string;
  /**
   * A signed-out visitor on a lookbook that allows guests: they comment
   * without signing in, under an optional name.
   */
  guest?: boolean;
  /**
   * Which board the reader came in from. Recorded on the comment so the deal
   * profile can tell public-board and global-board comments apart later; the
   * two share one thread, so nothing else can.
   */
  origin: RaiseBoardCommentOrigin;
}) {
  const { isSignedIn } = useUser();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [commentAuthOpen, setCommentAuthOpen] = useState(false);
  const [guestName, setGuestNameState] = useState(getGuestName);
  // Delete is offered for comments posted this session; matching a user's
  // older comments needs their db id on the client (follow-up).
  const [ownIds, setOwnIds] = useState<Set<string>>(new Set());

  // Scope belongs in the key as well as the request — the same deal has a
  // different thread in each lookbook than it does on the public board.
  const key = ["raise-board-comments", applicationId, lookbookPublicId ?? "global"];
  const { data: comments, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => raiseBoardApi.getComments(applicationId, lookbookPublicId),
  });

  const addComment = useMutation({
    mutationFn: (t: string) =>
      guest && lookbookPublicId
        ? raiseBoardApi.addGuestComment(
            applicationId,
            t,
            lookbookPublicId,
            getVisitorId(),
            guestName.trim() || undefined
          )
        : raiseBoardApi.addComment(applicationId, t, lookbookPublicId, origin),
    onSuccess: (res) => {
      setText("");
      // Remembered for their next comment, on this lookbook or another.
      if (guest) setGuestName(guestName);
      // Delete is a signed-in action, so a guest's comment never offers it.
      if (!guest) setOwnIds((prev) => new Set(prev).add(res.comment._id));
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e: unknown) =>
      toast.error(getErrorMessage(e, "Failed to post your comment")),
  });

  const deleteComment = useMutation({
    mutationFn: (id: string) => raiseBoardApi.deleteComment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
    onError: (e: unknown) =>
      toast.error(getErrorMessage(e, "Failed to delete the comment")),
  });

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    if (!isSignedIn && !guest) {
      setCommentAuthOpen(true);
      return;
    }
    addComment.mutate(t);
  };

  const list: RaiseBoardComment[] = comments ?? [];

  return (
    <RBCard>
      <div className="flex items-center gap-2">
        <MessageSquare className="h-[18px] w-[18px] text-muted-foreground" />
        <h2 className="text-lg font-semibold">Comments</h2>
        {list.length > 0 && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {list.length}
          </span>
        )}
      </div>

      {/* Composer. Unauthenticated visitors can still type — the sign-in
          dialog opens on submit and their draft survives it. */}
      <div className="mt-4">
        {guest && (
          <Input
            value={guestName}
            onChange={(e) => setGuestNameState(e.target.value.slice(0, 60))}
            maxLength={60}
            placeholder="Your name (optional)"
            aria-label="Your name (optional)"
            className="mb-2 rounded-xl"
          />
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, COMMENT_MAX))}
          rows={3}
          maxLength={COMMENT_MAX}
          placeholder={
            isSignedIn || guest ? "Add a comment…" : "Sign in to leave a comment"
          }
          className="w-full resize-none rounded-xl border border-border bg-card px-4 py-3 text-sm leading-relaxed outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/30"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
        />
        <div className="mt-2.5 flex items-center justify-between">
          <span className="text-xs tabular-nums text-muted-foreground">
            {text.length}/{COMMENT_MAX}
          </span>
          <Button
            className="rounded-full px-5"
            onClick={submit}
            disabled={addComment.isPending || !text.trim()}
          >
            {addComment.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {isSignedIn || guest ? "Post" : "Sign in to post"}
          </Button>
        </div>
      </div>

      <div className="mt-5">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            Couldn't load the comments. Refresh to try again.
          </p>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border py-10 text-center">
            <MessageSquare className="h-6 w-6 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              No comments yet. Be the first to share a thought.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {list.map((c) => (
              <li key={c._id} className="flex gap-3 py-4 first:pt-0 last:pb-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground/70">
                  {(c.user_name ?? "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">
                      {c.user_name ?? "Anonymous"}
                    </span>
                    {/* A guest picks their own name, so it is marked as
                        unverified rather than passing for a member's. */}
                    {c.user === null && c.user_name !== "Guest" && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Guest
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {timeAgo(c.created_on)}
                    </span>
                    {ownIds.has(c._id) && (
                      <button
                        type="button"
                        onClick={() => deleteComment.mutate(c._id)}
                        disabled={deleteComment.isPending}
                        className="ml-auto text-muted-foreground transition-colors hover:text-destructive"
                        aria-label="Delete comment"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-line break-words text-sm leading-relaxed text-foreground/85">
                    {c.text}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <BoardAuthDialog
        open={commentAuthOpen}
        onOpenChange={setCommentAuthOpen}
        reason="Sign in to comment on this deal."
      />
    </RBCard>
  );
}
